import { useMessageActionContext } from '@/app/providers';
import { StarIcon } from '@/assets/images';
import { AppIcon, Checkbox, CustomButton } from '@/components';
import { getFileIcon } from '@/features/chats/utils';

import ChatProgressLoader from '@/features/chats/ui/components/Progress/ChatProgressLoader';
import MessageActionDropdown from '@/features/chats/ui/Conversation/components/MessageActionDropdown';
import { isGroupChat } from '@/features/chats/utils/jidIdFormat';
import { useMessageAction } from '@/features/chats/viewmodels/useMessageAction';
import { formatChatTime } from '@/features/timeformatter/timeStamp';
import { Username, UserProfile } from '@/features/users';
import { getLocalUserId, getMessageSenderId } from '@/shared/utils';
import { formatBytes } from '@/shared/utils/mediaUtils';
import PropTypes from 'prop-types';
import { memo, useMemo } from 'react';
import MessageStatus from '../MessageStatus';
import ReplyMessageCard from '../ReplyMessageCard';

const SENDER_CLASS = 'sender-chat chat-bubble ct-flex ct-rowReverse ct-aiEnd ct-gap10 ct-padding-lr8-rl16';
const RECEIVER_CLASS = 'receiver-chat chat-bubble ct-flex ct-gap10 ct-padding-lr8-rl16';
const SENDER_INNER = 'ct-flex ct-flexColEnd ct-relative ct-gap10';
const RECEIVER_INNER = 'ct-flex ct-flexCol ct-relative ct-gap10';

const FileBox = memo(({ message, activeChatJid, showProfile, senderId }) => {
   const hasReply = message.msgBody?.replyTo;
   const { fileName, fileSizeDisplay, FileIcon } = useMemo(() => {
      const { media = {} } = message.msgBody ?? {};
      const { fileName = '', file_size = 0 } = media;
      const fileExt = fileName.split('.').pop() ?? '';
      return {
         fileName,
         fileSizeDisplay: file_size ? formatBytes(file_size, 2) : '',
         FileIcon: getFileIcon(fileExt),
      };
   }, [message.msgBody]);

   return (
      <div className={`chat-box ct-flex ct-flexCol ct-gap10 ct-relative`}>
         {showProfile && <Username className="cts-text ct-username" userId={senderId} />}
         <ReplyMessageCard replyTo={message.msgBody?.replyTo} activeChatJid={activeChatJid} />
         <div className="document-chat ct-flex ct-flexCol ct-gap12">
            <div className={`document-info ct-flex ct-aiCenter ct-gap10 ${hasReply ? 'reply-bubble' : ''}`}>
               <AppIcon icon={FileIcon} className="ct-flexCenter ct-icon-doc" />
               <span className="ct-ellipsis">{fileName}</span>
               <ChatProgressLoader message={message} loaderSize="md" />
            </div>
            {fileSizeDisplay && <p className="document-size">{fileSizeDisplay}</p>}
         </div>
      </div>
   );
});

FileBox.displayName = 'FileBox';
FileBox.propTypes = {
   message: PropTypes.object.isRequired,
   activeChatJid: PropTypes.string,
   showProfile: PropTypes.bool,
   senderId: PropTypes.string,
};

const FileCard = memo(({ message, activeChatJid }) => {
   const { hideAvatar = false, hideActions = false } = useMessageActionContext();
   const { isSelected, isSelectionActive, handleToggleSelection } = useMessageAction(message, activeChatJid);
   const senderId = getMessageSenderId({ type: 'chats', message });
   const isSender = senderId === getLocalUserId();

   const timestamp = useMemo(
      () => (message.timestamp ? formatChatTime(new Date(Number(message.timestamp)), 'message-card') : ''),
      [message.timestamp],
   );
   const hideAvatarFileCard = isSender || hideAvatar;
   const isChatGroup = useMemo(() => isGroupChat(activeChatJid), [activeChatJid]);
   const showProfile = isChatGroup && !hideAvatarFileCard;

   const isHighlighted = message?.isHighlighted;

   return (
      <CustomButton
         as="div"
         className={`message-row custom-file-card ${isSelectionActive && !hideActions ? 'cts-file ct-flex ct-aiCenter selection-active' : ''} ${isSelected && !hideActions ? 'selected' : ''} ${isHighlighted && !hideActions ? 'search-highlight-active' : ''}`}
         onClick={isSelectionActive && !hideActions ? handleToggleSelection : null}
      >
         {isSelectionActive && !hideActions && (
            <div className="selection-checkbox-wrap cts-file">
               <Checkbox checked={isSelected} onChange={handleToggleSelection} />
            </div>
         )}
         <div className={`cts-file ${isSender ? SENDER_CLASS : RECEIVER_CLASS}`}>
            {showProfile && (
               <picture className="chat-avatar cts-file">
                  <UserProfile userId={senderId} className="chat-avatar chat-avatar-fallback ct-flexCenter" />
               </picture>
            )}
            <div
               className={`${isSender ? SENDER_INNER : RECEIVER_INNER} ${message.favouriteStatus === 1 ? 'starredMessage' : ''}`}
            >
               <FileBox message={message} activeChatJid={activeChatJid} showProfile={showProfile} senderId={senderId} />
               <div className={`cts-file ct-flexAlign ct-flex ct-gap8 ${isSender ? 'ct-rowReverse' : ''}`}>
                  {isSender && <MessageStatus msgStatus={message.msgStatus} />}
                  <span className="time cts-file">{timestamp}</span>
                  {message.favouriteStatus === 1 && (
                     <i className="icon starIcon ct-flexCenter">
                        <StarIcon />
                     </i>
                  )}
               </div>
               {!hideActions && (
                  <MessageActionDropdown
                     className="cts-file"
                     message={message}
                     isSender={isSender}
                     activeChatJid={activeChatJid}
                  />
               )}
            </div>
         </div>
      </CustomButton>
   );
});

FileCard.displayName = 'FileCard';
FileCard.propTypes = {
   message: PropTypes.object.isRequired,
   activeChatJid: PropTypes.string,
};

export default FileCard;
